import { formatDate } from '@angular/common';
import { KalDate, KalDateType } from '@kalidea/kaligraphi';
import { environment } from 'src/environments/environment';

export type filterOperator = '<=' | '>=' | 'Entre';

export type rangeDateType = { startDate: KalDate, endDate: KalDate };

export type RangeToFiltersType = { operator: filterOperator, start: string, end: string };

export function rangeToFilters(rangeDate: rangeDateType, {toUtc, shouldResetTime} = {toUtc: false, shouldResetTime: false}): RangeToFiltersType {

    const {startDate, endDate} = rangeDate || {startDate: null, endDate: null};
    let operator: filterOperator;
    let start: any;
    let end: any;

    if (startDate) {
        // a vérifier le fuseau horaire pour ne pas avoir de soucis
        start = toUtc ? getUTCDate(startDate, {shouldResetTime}) : getISODate(startDate);
        operator = '>=';
    }
    if (endDate) {
        end = toUtc ? getUTCDate(endDate, {shouldResetTime}) : getISODate(endDate);
        operator = '<=';
    }
    if (startDate && endDate) {
        operator = 'Entre';
    }

    return {operator, start, end};
}

export function getDuration(date: string, withMonth = true): string | null {
    if (!date) {
        return null;
    }

    const startComparisonDate = new KalDate(date, environment.date.format.isoQuery);
    const endComparisonDate = new KalDate();
    let dateStr = '';

    const truncatedYears = Math.floor(endComparisonDate.diff(startComparisonDate, 'years'));
    const truncatedMonths = Math.floor(endComparisonDate.diff(startComparisonDate, 'months'));

    const truncatedDays = Math.floor(endComparisonDate.diff(startComparisonDate, 'days'));

    // display years
    if (truncatedYears > 0) {
        dateStr = truncatedYears + ' an' + (truncatedYears > 1 ? 's' : '');
    }

    const months = truncatedMonths - (truncatedYears * 12);

    // there are cases where we don't want to display months but the year diff
    // can be 0 so we have to display the months to not have an empty date
    if (months > 0 && (withMonth || truncatedYears === 0)) {
        if (truncatedYears > 0) {
            dateStr += ' et ';
        }

        dateStr += months + ' mois';
    }

    // display only days of the date is less than 1 month
    if (truncatedYears === 0 && truncatedMonths === 0 && truncatedDays > 0) {
        dateStr = truncatedDays + ' jour' + (truncatedDays > 1 ? 's' : '');
    }

    return dateStr;
}

export function getKalDateFromObject<T, K extends keyof T>(obj: T, dateProperty: K, parseFormat = environment.date.format.isoQuery): KalDate | null {
    const date = obj && obj[dateProperty] ? new KalDate(obj[dateProperty] as any, parseFormat) : null;
    return date?.valid ? date : null;
}

/**
 * Met l'horaire d'une date à minuit
 * @param date
 */
export function toMidnightUTC(date?: KalDateType): KalDate | null {
    if (!date) {
        return null;
    }

    const rawDate = (date instanceof KalDate ? date : new KalDate(date)).toDate();

    rawDate.setUTCHours(0, 0, 0, 0);

    return new KalDate(rawDate);
}

export function getISODate(date?: KalDateType, parseFormat: string = environment.date.format.isoQuery): string | null {
    if (!date) {
        return null;
    }

    return (date instanceof KalDate ? date : new KalDate(date, parseFormat)).toFormat(environment.date.format.isoQuery);
}

export function getUTCDate(dateInput: KalDate | string | null, {shouldResetTime} = {shouldResetTime: false}): string | null {
    if (!dateInput) {
        return null;
    }

    const isKalDate = dateInput instanceof KalDate;
    const isAlreadyUTC = !isKalDate && !!(dateInput as string).endsWith('Z');

    // Si la date passée en paramètre est déjà en UTC, alors inutile de poursuivre
    if (isAlreadyUTC) {
        return dateInput as string;
    }

    if (shouldResetTime) {
        dateInput = (!isKalDate ? new KalDate(dateInput) : dateInput) as KalDate;
        return new Date(
            Date.UTC(
                dateInput.getYear(),
                dateInput.getMonth()-1,
                dateInput.getDay(),
                0, 0, 0, 0
            )
        ).toISOString();
    }

    return new Date((dateInput as KalDate).toDate().toUTCString()).toISOString();
}

export function isBetween(currentDate: KalDate, startDate: KalDate, endDate: KalDate): boolean {
    return (!startDate || currentDate.isSameOrAfter(startDate)) && (!endDate || currentDate.isSameOrBefore(endDate));
}

export function getRangeDateLabel(startDate: string, endDate: string, parseFormat = environment.date.format.isoQuery): string | null {
    const [selectedStartDate, selectedEndDate] = [startDate, endDate].map(e => {
        return e ? new KalDate(e, parseFormat)?.toFormat(environment.date.format.display) : null;
    });

    if (selectedStartDate && selectedEndDate) {
        return `Du ${selectedStartDate} au ${selectedEndDate}`;
    } else if (selectedStartDate) {
        return `A partir du ${selectedStartDate}`;
    } else if (selectedEndDate) {
        return `Jusqu'à ${selectedEndDate}`;
    }

    return null;
}

export function getAgeRangeLabel(ageMin: number, ageMax: number): string | null {
    if (ageMin && ageMax) {
        return `De ${ageMin} à ${ageMax} ans`;
    } else if (ageMin) {
        return `A partir de ${ageMin} ans`;
    } else if (ageMax) {
        return `Jusqu'à ${ageMax} ans`;
    }
    return null;
}

/**
 * return oldest date of twos
 */
export function oldest(a: KalDate, b: KalDate): KalDate {
    return a?.isAfter(b) ? b : a;
}

/**
 * return oldest date of twos
 */
export function closest(a: KalDate, b: KalDate): KalDate {
    return a?.isBefore(b) ? b : a;
}

/**
 * Return Hours and Minutes formated for an input[type=time]
 */
export function extractHourAndMinuteFromKalDate(date: KalDate): string {
    if (!date) {
        return '';
    }
    const hour = date.getDate().hour;
    const minute = date.getDate().minute;
    return `${hour < 10 ? '0' + hour : hour}:${minute < 10 ? '0' + minute : minute}`;
}

/**
 * @param time formatted as hh:mm
 * @param date to which time is added (new KalDate by default)
 */
export function timeToISODate(date: KalDate, time: string): string | null {
    if (!time) {
        return getISODate(date);
    }

    return getISODate(date
            .set('hours', +time.substring(0, 2))
            .set('minutes', +time.substring(3, 5))
        );
}

/**
 * @param time formatted as hh:mm
 * @param date KalDate
 */
export function timeToKalDate(date: KalDate, time: string): KalDate | null | undefined {
    if(!date) {
        return null;
    }

    const [heures, minutes] = time?.split(':' ) || [];

    if (!!heures && !!minutes) {
        return (date
            .set('hours', +heures)
            .set('minutes', +minutes)
        );
    }

}

/**
 * Formate correctement la date d'un champ perso de type date
 * À utiliser pour tous les champs perso de type date
 * voir #51715
 * traite les formats :
 * 1996-09-01T00:00:00.000+02:00
 * 1998-11-08T00:00:00.000+01:00
 * 2017-11-21T00:00:00.000Z
 * 31/12/2015 00:00:00
 * 6/7/2023 12:00:00 AM (anglais)
 * 06/07/2023
 *
 * depuis #52176 - traite ces formats supplémentaires :
 * 06/7/2023
 * 6/07/2023
 * 6/7/2023
 */
export function formatCustomFieldDate(date: string, locale: string): KalDate | null {
    if (!!date) {
        const {human, display} = environment.date.format;

        const isDateInvalid = !(new KalDate(date).valid);

        if(isDateInvalid && date.match(/([0-9]{1,2})(?:\/|-)([0-9]{1,2})(?:\/|-)([0-9]{4})/g)) {
            const dateSeparator = date.includes('/') ? '/' : '-';
            let dateElementList = date.split(dateSeparator).map(s => s.padStart(2, '0'));

            // Dans le cas d'un format anglais (avec AM/PM), le mois et le jour sont inversés.
            // Il faut donc les remettre en place pour ne pas afficher une fausse date
            if(dateElementList[2].match(/AM|PM/gi)) {
                dateElementList = [
                    dateElementList[1], // jour
                    dateElementList[0], // mois
                    dateElementList[2] // année
                ];
            }

            date = dateElementList.join(dateSeparator);
        }

        const formattedDate = new KalDate(date, human)?.toFormat(display) || formatDate(date, display, locale);

        return new KalDate(formattedDate?.substr(0, 10));
    }
    return null;
}

/**
 * Vrai si la date correspond à une sentinel back « pas de date significative » (année ≤ 1900) :
 * - 0001-01-01 (Date.MinValue .NET)
 * - 1753-01-01 (DATETIME.MinValue SQL Server)
 * - 1900-01-01 (sentinel SQL conventionnelle, peut tomber en 1899 après conversion locale LMT)
 * Une date null, undefined ou invalide n'est PAS sentinel (utiliser .valid en amont si besoin).
 */
export function isSentinelDate(date: KalDate | null | undefined): boolean {
    return !!date?.valid && date.getYear() <= 1900;
}
